import {
  DEFAULT_VIDEO_PROJECT_TIMEOUT_MS,
  buildVideoProjectConfig,
  toFriendlyVideoGenerationError,
  waitForVideoProject
} from './video-project.js';
import { fitImageBlobToVideoCanvas } from './video-image.js';
import { assertRequestOwner } from './generation-request.js';
import { isSdkLocalImageTimeout } from './image-delivery-status.js';
import { jobSensitive } from './job-completion.js';

/**
 * Orchestrate one user-owned Gallery video with injectable SDK boundaries.
 * The dependency seam keeps billing/model/payload/result behavior testable
 * without a live account or browser canvas.
 */
export async function createGalleryVideo(referenceImage, options = {}, dependencies = {}) {
  const {
    modelId,
    prompt,
    width,
    height,
    fps,
    durationSeconds,
    modelParameters,
    generateAudio,
    network = 'fast',
    ratio,
    billingMode,
    accountId,
    seed,
    attribution,
    beforeSubmit,
    onSubmitted,
    onSubmissionFailure,
    runControl,
    timeout = DEFAULT_VIDEO_PROJECT_TIMEOUT_MS
  } = options;
  const {
    getVideoModel,
    createVideoProject,
    approveProject,
    getAccountId,
    lookupSensitive,
    fitImageBlob = fitImageBlobToVideoCanvas
  } = dependencies;

  if (typeof getVideoModel !== 'function' || typeof createVideoProject !== 'function') {
    throw new Error('Video SDK dependencies are unavailable.');
  }

  const { settings, projectConfig } = buildVideoProjectConfig({
    referenceImage,
    prompt,
    modelId,
    width,
    height,
    fps,
    durationSeconds,
    modelParameters,
    generateAudio,
    network,
    ratio,
    seed,
    timeout
  });

  let releaseSessionGuard = null;
  let createdProjectId = null;
  let createdProject = null;
  let createAttempted = false;
  let runGuard = null;
  let delivery = null;
  try {
    runControl?.assertCanSubmit();
    assertRequestOwner({ accountId }, getAccountId());
    const modelInfo = await getVideoModel(settings.modelId, network);
    if (!modelInfo || Number(modelInfo.workerCount) <= 0) {
      throw new Error('The selected video model has no workers available right now.');
    }

    projectConfig.referenceImage = await fitImageBlob(
      referenceImage,
      settings.width,
      settings.height,
      { opaque: settings.family === 'wan3' }
    );

    assertRequestOwner({ accountId }, getAccountId());
    runControl?.assertCanSubmit();
    const reviewed = await approveProject({ ...projectConfig, ...(billingMode ? { billingMode } : {}),
      ...(attribution ? { attribution } : {}) }, { accountId });
    assertRequestOwner(reviewed, getAccountId());
    runControl?.assertCanSubmit();
    await beforeSubmit?.(reviewed);
    assertRequestOwner(reviewed, getAccountId());
    runControl?.assertCanSubmit();
    delivery = runControl?.beginSubmission();
    createAttempted = true;
    const result = await createVideoProject(reviewed.projectConfig, { request: reviewed });
    if (!result?.project?.id) {
      throw Object.assign(new Error('This video submission could not be confirmed. Check your existing Sogni creations before generating again.'),
        { code: 'VIDEO_RESULT_UNVERIFIED', unverified: true });
    }
    releaseSessionGuard = result.releaseSessionGuard || null;
    createdProjectId = result.project.id;
    createdProject = result.project;
    runGuard = delivery?.accepted(result.project);
    await onSubmitted?.(result.project);
    assertRequestOwner(reviewed, getAccountId());

    const videoUrls = await waitForVideoProject(
      result.project,
      timeout,
      runGuard ? Promise.race([runGuard.invalidated, ...(result.sessionInvalidated ? [result.sessionInvalidated] : [])]) : result.sessionInvalidated
    );
    const videoUrl = videoUrls?.[0];
    if (!videoUrl) {
      const error = new Error('This existing video has no download link yet. Check the project in your Sogni creations before generating it again.');
      error.code = 'VIDEO_RESULT_UNVERIFIED';
      error.unverified = true;
      throw error;
    }
    assertRequestOwner(reviewed, getAccountId());
    // The server labels a delivered clip the same way as an image. The live
    // frame carries the label, but a completion the SDK replayed after a
    // reconnect does not and looks the same, so an unlabelled clip reads its
    // record once. The read guards a delivered clip: when it fails, the clip
    // stays as the live frame labelled it.
    const job = result.project.jobs?.[0];
    let sensitive = jobSensitive(job);
    if (!sensitive && lookupSensitive) {
      try {
        sensitive = await lookupSensitive(createdProjectId);
      } catch (error) {
        console.warn(`[Video] Sensitive label unavailable for ${createdProjectId}:`, error?.message || error);
      }
      assertRequestOwner(reviewed, getAccountId());
    }

    return {
      ...result,
      projectId: createdProjectId,
      completed: true,
      videoUrls,
      videoUrl,
      sensitive,
      model: modelInfo.id,
      settings: {
        durationSeconds: settings.durationSeconds,
        fps: settings.fps,
        width: settings.width,
        height: settings.height,
        generateAudio: projectConfig.generateAudio,
        actualDurationSeconds: settings.timing.actualDurationSeconds,
        frames: settings.frames,
        network,
        outputWidth: settings.width * (settings.model.outputScale || 1),
        outputHeight: settings.height * (settings.model.outputScale || 1)
      }
    };
  } catch (error) {
    const friendly = toFriendlyVideoGenerationError(error);
    if (createdProjectId) friendly.projectId = createdProjectId;
    if (createdProjectId && friendly.code === 'REQUEST_CHANGED') friendly.unverified = true;
    // Cancellation is requested at VIDEO_TIMEOUT; it has not yet been
    // confirmed. Connection loss during create/completion is also ambiguous.
    if (createAttempted && (friendly.code === 'VIDEO_TIMEOUT' || isSdkLocalImageTimeout(error)
      || /network|socket|connection|transport|timed out|timeout/i.test(error.message || ''))) friendly.unverified = true;
    delivery?.failed(friendly.unverified === true);
    await onSubmissionFailure?.(friendly, { createAttempted, projectStatus: createdProject?.status });
    throw friendly;
  } finally {
    runGuard?.release();
    releaseSessionGuard?.();
  }
}
